import { useEffect, useRef } from "react";

/** A form on screen. With `saveDraft`, a forced reload saves it and goes on; without, the reload waits. */
interface Hook {
  dirty: boolean;
  label: string;
  saveDraft?: () => unknown;
}

const hooks = new Map<number, Hook>();
let nextId = 1;
const listeners = new Set<() => void>();
const tell = () => listeners.forEach((listener) => listener());

/** The path where the user typed. */
let typedPath: string | null = null;
let shownPath: string | null = null;
let reloading = false;

/** Register the form of a page for the forced reload of a new version (rule U-35). */
export function useHoldReload(dirty: boolean, label: string, saveDraft?: () => unknown): void {
  const id = useRef(0);
  if (!id.current) id.current = nextId++;
  const draft = useRef(saveDraft);
  draft.current = saveDraft;
  const hasDraft = saveDraft !== undefined;
  useEffect(() => {
    const key = id.current;
    hooks.set(key, { dirty, label, saveDraft: hasDraft ? () => draft.current?.() : undefined });
    tell();
  }, [dirty, label, hasDraft]);
  useEffect(() => {
    const key = id.current;
    return () => {
      hooks.delete(key);
      tell();
    };
  }, []);
}

/** The labels of the dirty forms that have no draft: each one stops a reload. */
export function holds(): string[] {
  return [...new Set([...hooks.values()].filter((hook) => hook.dirty && !hook.saveDraft).map((hook) => hook.label))];
}

/** True when the user typed in the page on screen. It stops only a silent reload, not the countdown. */
export function edited(): boolean {
  return typedPath !== null && typedPath === shownPath;
}

/** The dirty forms that a reload saves first. */
export function dirtyDrafts(): number {
  return [...hooks.values()].filter((hook) => hook.dirty && hook.saveDraft).length;
}

/** Save each dirty draft; a failed save is logged and the reload goes on, as the form keeps its own copy too. */
export async function saveDrafts(log: (message: string, detail: Record<string, unknown>) => void): Promise<number> {
  const drafts = [...hooks.values()].filter((hook) => hook.dirty && hook.saveDraft);
  let saved = 0;
  for (const hook of drafts) {
    try {
      await hook.saveDraft!();
      saved += 1;
    } catch (error) {
      log("draft save failed", { label: hook.label, error: String(error) });
    }
  }
  return saved;
}

export function onHoldChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const SKIP_TYPED = 'nav, header, [role="search"], [data-no-hold], input[type="search"], [data-testid="update-ready"]';

/** An input or a change in a page control is an edit; a search box, the menu and the header are not. */
export function markTyped(target: EventTarget | null): void {
  const element = target && typeof (target as Element).closest === "function" ? (target as Element) : null;
  if (!element || element.closest(SKIP_TYPED)) return;
  if (typedPath !== shownPath) {
    typedPath = shownPath;
    tell();
  }
}

/** A tap counts as an edit only on a control marked `data-hold`, so a sort header or a chip never holds a reload. */
export function markTap(target: EventTarget | null): void {
  const element = target && typeof (target as Element).closest === "function" ? (target as Element) : null;
  if (element?.closest("[data-hold]")) markTyped(target);
}

export function setShownPath(path: string): void {
  shownPath = path;
  tell();
}

/** True while the app reloads for a new version, so that the pages do not ask "Leave site?". */
export function isReloading(): boolean {
  return reloading;
}

export function setReloading(value: boolean): void {
  reloading = value;
}

/** For tests. */
export function resetHolds(): void {
  hooks.clear();
  listeners.clear();
  typedPath = null;
  shownPath = null;
  reloading = false;
}

/** For tests: register a hook without React. */
export function addHookForTests(hook: Hook): () => void {
  const key = nextId++;
  hooks.set(key, hook);
  return () => hooks.delete(key);
}
